import * as React from 'react';
import { useClientDashboard } from '../../hooks/useClientDashboard';
import { useBacs } from '../../hooks/useBacs';
import { useAuth } from '../../core/context/ProvidersContext';
import { KpiCards } from '../clients/dashboard/KpiCards';
import { DepotMap } from '../clients/dashboard/DepotMap';
import { CapacityChart } from '../clients/dashboard/CapacityChart';
import { CapacityTable } from '../clients/dashboard/CapacityTable';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import appStyles from '../App.module.scss';
import styles from './ClientDashboardView.module.scss';

export const ClientDashboardView: React.FC = () => {
  const { user, isExternalClient } = useAuth();
  // External clients are forced to their own clientId (backend ignores query
  // param anyway, but this keeps the UI consistent).
  const [selectedClientId, setSelectedClientId] = React.useState<number | null>(
    isExternalClient ? (user?.clientId ?? null) : null
  );

  const { clients, depotsGeo, capacites, synthese, loading, error } = useClientDashboard(selectedClientId);
  const { bacs } = useBacs();

  const selectedClient = clients.find(c => c.Id === selectedClientId);
  const clientName = selectedClient ? selectedClient.RaisonSociale : 'Tous les clients';

  // Sync when the auth context gives us a clientId after initial mount.
  React.useEffect(() => {
    if (isExternalClient && user?.clientId && selectedClientId !== user.clientId) {
      setSelectedClientId(user.clientId);
    }
  }, [isExternalClient, user?.clientId, selectedClientId]);

  if (loading) {
    return (
      <div className={appStyles.loading}>
        <Spinner size={SpinnerSize.medium} label="Chargement des données clients..." />
      </div>
    );
  }

  if (error) {
    return <div className={appStyles.error}>{error}</div>;
  }

  return (
    <div className={styles.dashboard}>
      {/* Client selector — masqué pour les clients externes */}
      {!isExternalClient && (
        <div className={styles.selectorRow}>
          <div className={styles.selectorLabel}>Client</div>
          <select
            className={styles.selector}
            value={selectedClientId || ''}
            onChange={e => setSelectedClientId(e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">Tous les clients</option>
            {clients.map(c => (
              <option key={c.Id} value={c.Id}>
                {c.RaisonSociale} ({c.Type})
              </option>
            ))}
          </select>
          {selectedClient && (
            <div className={styles.clientInfo}>
              <span className={styles.clientBadge}>{selectedClient.Type}</span>
              <span className={styles.clientContact}>{selectedClient.ContactPrincipal}</span>
              <span className={styles.clientEmail}>{selectedClient.Email}</span>
            </div>
          )}
        </div>
      )}

      {/* Bandeau d'identification pour les clients externes */}
      {isExternalClient && selectedClient && (
        <div className={styles.clientBanner}>
          <div className={styles.clientBannerLeft}>
            <div className={styles.clientBannerTitle}>{selectedClient.RaisonSociale}</div>
            <div className={styles.clientBannerSubtitle}>
              Tableau de bord — {selectedClient.Type}
            </div>
          </div>
          <div className={styles.clientBannerRight}>
            <span className={styles.clientBadge}>{selectedClient.Type}</span>
            {selectedClient.ContactPrincipal && (
              <span className={styles.clientContact}>{selectedClient.ContactPrincipal}</span>
            )}
          </div>
        </div>
      )}

      {/* KPI Cards */}
      <KpiCards capacites={capacites} bacs={bacs} clientName={clientName} />

      {/* Map + Chart row */}
      <div className={styles.vizRow}>
        <DepotMap depots={depotsGeo} capacites={capacites} />
        <CapacityChart capacites={capacites} />
      </div>

      {/* Detail table */}
      <CapacityTable capacites={capacites} />
    </div>
  );
};
